// src/features/insights/services/aiMemoryService.js
// Consolidated AI Memory Service - 2026 Architecture
//
// Manages unified userAIMemory collection that consolidates:
// - userMemory (engagement tracking) - MIGRATED & DELETED
// - userPatterns (pattern detection) - MIGRATED & DELETED
// - userStatistics (writing statistics) - MIGRATED & DELETED
//
// Schema version: 2.0 (consolidated)
// Migration completed: February 2026

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/shared/utils/core/firebase-config';
import logger from '@/shared/utils/core/logger';

// Schema version for migration tracking
const AI_MEMORY_SCHEMA_VERSION = '2.0';

// Collection name for consolidated AI memory
const COLLECTION_NAME = 'userAIMemory';

/**
 * Generate a sync token for offline-first sync
 */
function generateSyncToken() {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create empty consolidated memory structure
 */
function createEmptyMemory(userId) {
  return {
    uid: userId,

    // === ENGAGEMENT (from userMemory) ===
    engagement: {
      engagedInsights: [],      // Last 100
      dismissedInsights: [],    // Last 50
      feedbackPreferences: {
        preferredTone: 'balanced',    // analytical, empathetic, balanced
        preferredLength: 'detailed',  // concise, detailed
        likesQuestions: true,
        likesChallenges: true,
        preferredInsightTypes: []
      },
      successfulAdvice: [],
      userIntentions: []
    },

    // === PATTERNS (from userPatterns) ===
    patterns: {
      recurringThemes: [],      // Top 10
      emotionalPatterns: [],
      triggers: [],
      copingMechanisms: [],
      writingStyle: {
        vocabularyDiversity: 0,
        averageSentenceLength: 0,
        commonPhrases: []
      },
      lastAnalyzed: null
    },

    // === STATISTICS (from userStatistics) ===
    statistics: {
      totalEntries: 0,
      totalWords: 0,
      averageEntryLength: 0,
      writingFrequency: { daily: 0, weekly: 0, monthly: 0 },
      currentStreak: 0,
      longestStreak: 0,
      lastEntryDate: null,
      moodDistribution: {},
      dominantMood: null,
      moodTrend: 'stable',
      sentimentTrajectory: { last7Days: 0, last30Days: 0, last90Days: 0 },
      preferredWritingTime: null,
      mostProductiveDays: []
    },

    // === METADATA ===
    syncToken: generateSyncToken(),
    lastUpdated: Timestamp.now(),
    version: AI_MEMORY_SCHEMA_VERSION
  };
}

/**
 * Get user's AI memory from Firestore (consolidated)
 * Falls back to legacy collections during migration
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} - User memory object
 */
export async function getUserMemory(userId) {
  if (!userId) {
    logger.warn('[AIMemory] No user ID provided');
    return null;
  }

  try {
    // Read from consolidated userAIMemory collection
    const memoryRef = doc(db, COLLECTION_NAME, userId);
    const memoryDoc = await getDoc(memoryRef);

    if (memoryDoc.exists()) {
      return memoryDoc.data();
    }

    // No memory found, return default empty structure
    logger.debug('[AIMemory] No memory found for user, creating default');
    return createEmptyMemory(userId);

  } catch (error) {
    logger.error('[AIMemory] Failed to get user memory:', error);
    return null;
  }
}

/**
 * Save consolidated AI memory
 * @param {string} userId - User ID
 * @param {Object} memory - Memory object to save
 */
async function saveMemory(userId, memory) {
  try {
    const memoryRef = doc(db, COLLECTION_NAME, userId);
    await setDoc(memoryRef, {
      ...memory,
      syncToken: generateSyncToken(),
      lastUpdated: Timestamp.now(),
      version: AI_MEMORY_SCHEMA_VERSION
    }, { merge: true });
  } catch (error) {
    logger.error('[AIMemory] Failed to save memory:', error);
    throw error;
  }
}

/**
 * Track insight engagement
 * @param {string} userId - User ID
 * @param {string} insightId - Insight ID
 * @param {Object} engagementData - Engagement metrics
 */
export async function trackInsightEngagement(userId, insightId, engagementData) {
  if (!userId || !insightId) {
    logger.warn('[AIMemory] Missing userId or insightId for engagement tracking');
    return;
  }

  try {
    const memory = await getUserMemory(userId) || createEmptyMemory(userId);

    const engagement = {
      insightId,
      insightType: engagementData.insightType || 'general',
      content: engagementData.content?.substring(0, 200) || '',
      userResponse: engagementData.response || 'engaged',
      engagementTime: engagementData.timeSpent || 0,
      timestamp: Timestamp.now()
    };

    // Keep last 100 engaged insights
    const engagedInsights = [...(memory.engagement?.engagedInsights || []), engagement].slice(-100);

    await saveMemory(userId, {
      engagement: {
        ...memory.engagement,
        engagedInsights
      }
    });

    logger.debug('[AIMemory] Tracked insight engagement:', { userId, insightId });

    // Update preferences based on engagement
    await updateFeedbackPreferences(userId);

  } catch (error) {
    logger.error('[AIMemory] Failed to track engagement:', error);
  }
}

/**
 * Track insight dismissal
 * @param {string} userId - User ID
 * @param {string} insightId - Insight ID
 * @param {Object} dismissalData - Dismissal information
 */
export async function trackInsightDismissal(userId, insightId, dismissalData = {}) {
  if (!userId || !insightId) {
    logger.warn('[AIMemory] Missing userId or insightId for dismissal tracking');
    return;
  }

  try {
    const memory = await getUserMemory(userId) || createEmptyMemory(userId);

    const dismissal = {
      insightId,
      insightType: dismissalData.insightType || 'general',
      content: dismissalData.content?.substring(0, 200) || '',
      dismissedAt: Timestamp.now(),
      dismissalSpeed: dismissalData.dismissalSpeed || 'unknown'
    };

    // Keep last 50 dismissed insights
    const dismissedInsights = [...(memory.engagement?.dismissedInsights || []), dismissal].slice(-50);

    await saveMemory(userId, {
      engagement: {
        ...memory.engagement,
        dismissedInsights
      }
    });

    logger.debug('[AIMemory] Tracked insight dismissal:', { userId, insightId });

    await updateFeedbackPreferences(userId);

  } catch (error) {
    logger.error('[AIMemory] Failed to track dismissal:', error);
  }
}

/**
 * Infer and update feedback preferences based on engagement patterns
 */
async function updateFeedbackPreferences(userId) {
  try {
    const memory = await getUserMemory(userId);
    if (!memory?.engagement?.engagedInsights) return;

    const engaged = memory.engagement.engagedInsights || [];

    if (engaged.length < 5) return;

    // Calculate preferred insight types
    const insightTypeCounts = {};
    engaged.forEach(e => {
      insightTypeCounts[e.insightType] = (insightTypeCounts[e.insightType] || 0) + 1;
    });

    const preferredInsightTypes = Object.entries(insightTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([type]) => type)
      .slice(0, 3);

    // Calculate preferred length based on engagement time
    const avgEngagementTime = engaged.reduce((sum, e) => sum + (e.engagementTime || 0), 0) / engaged.length;
    const preferredLength = avgEngagementTime > 30 ? 'detailed' : 'concise';

    const currentPreferences = memory.engagement?.feedbackPreferences || {};

    const updatedPreferences = {
      preferredTone: currentPreferences.preferredTone || 'balanced',
      preferredLength,
      likesQuestions: currentPreferences.likesQuestions !== false,
      likesChallenges: currentPreferences.likesChallenges !== false,
      preferredInsightTypes
    };

    await saveMemory(userId, {
      engagement: {
        ...memory.engagement,
        feedbackPreferences: updatedPreferences
      }
    });

    logger.debug('[AIMemory] Updated feedback preferences:', updatedPreferences);

  } catch (error) {
    logger.error('[AIMemory] Failed to update preferences:', error);
  }
}

/**
 * Track successful advice
 */
export async function trackSuccessfulAdvice(userId, adviceData) {
  if (!userId || !adviceData.advice) {
    logger.warn('[AIMemory] Missing userId or advice for tracking');
    return;
  }

  try {
    const memory = await getUserMemory(userId) || createEmptyMemory(userId);

    const successfulAdvice = {
      advice: adviceData.advice,
      situation: adviceData.situation || 'general',
      outcome: adviceData.outcome || 'improved',
      confidence: adviceData.confidence || 0.75,
      timestamp: Timestamp.now()
    };

    // Keep last 50 successful advice entries
    const allAdvice = [...(memory.engagement?.successfulAdvice || []), successfulAdvice].slice(-50);

    await saveMemory(userId, {
      engagement: {
        ...memory.engagement,
        successfulAdvice: allAdvice
      }
    });

    logger.info('[AIMemory] Tracked successful advice');

  } catch (error) {
    logger.error('[AIMemory] Failed to track successful advice:', error);
  }
}

/**
 * Track user intention
 */
export async function trackUserIntention(userId, intentionData) {
  if (!userId || !intentionData.intention) {
    logger.warn('[AIMemory] Missing userId or intention for tracking');
    return;
  }

  try {
    const memory = await getUserMemory(userId) || createEmptyMemory(userId);

    const intention = {
      intention: intentionData.intention,
      extractedFrom: intentionData.entryIds || [],
      firstMentioned: Timestamp.now(),
      progressMarkers: intentionData.progressMarkers || []
    };

    const existingIntentions = memory.engagement?.userIntentions || [];
    const existingIndex = existingIntentions.findIndex(
      i => i.intention.toLowerCase() === intentionData.intention.toLowerCase()
    );

    if (existingIndex >= 0) {
      existingIntentions[existingIndex].extractedFrom.push(...(intentionData.entryIds || []));
      existingIntentions[existingIndex].progressMarkers.push(...(intentionData.progressMarkers || []));
    } else {
      existingIntentions.push(intention);
    }

    await saveMemory(userId, {
      engagement: {
        ...memory.engagement,
        userIntentions: existingIntentions
      }
    });

    logger.debug('[AIMemory] Tracked user intention');

  } catch (error) {
    logger.error('[AIMemory] Failed to track intention:', error);
  }
}

/**
 * Update patterns (called by Cloud Functions)
 */
export async function updatePatterns(userId, patternsData) {
  if (!userId) return;

  try {
    await saveMemory(userId, {
      patterns: {
        recurringThemes: patternsData.recurringThemes || [],
        emotionalPatterns: patternsData.emotionalPatterns || [],
        triggers: patternsData.triggers || [],
        copingMechanisms: patternsData.copingMechanisms || [],
        writingStyle: patternsData.writingStyle || {},
        lastAnalyzed: Timestamp.now()
      }
    });

    logger.info('[AIMemory] Updated patterns');

  } catch (error) {
    logger.error('[AIMemory] Failed to update patterns:', error);
  }
}

/**
 * Update statistics (called by Cloud Functions or after entry sync)
 */
export async function updateStatistics(userId, statsData) {
  if (!userId) return;

  try {
    await saveMemory(userId, {
      statistics: {
        totalEntries: statsData.totalEntries || 0,
        totalWords: statsData.totalWords || 0,
        averageEntryLength: statsData.averageEntryLength || 0,
        writingFrequency: statsData.writingFrequency || { daily: 0, weekly: 0, monthly: 0 },
        currentStreak: statsData.currentStreak || 0,
        longestStreak: statsData.longestStreak || 0,
        lastEntryDate: statsData.lastEntryDate || null,
        moodDistribution: statsData.moodDistribution || {},
        dominantMood: statsData.dominantMood || null,
        moodTrend: statsData.moodTrend || 'stable',
        sentimentTrajectory: statsData.sentimentTrajectory || { last7Days: 0, last30Days: 0, last90Days: 0 },
        preferredWritingTime: statsData.preferredWritingTime || null,
        mostProductiveDays: statsData.mostProductiveDays || []
      }
    });

    logger.debug('[AIMemory] Updated statistics');

  } catch (error) {
    logger.error('[AIMemory] Failed to update statistics:', error);
  }
}

// ============================================================================
// ACCESSOR FUNCTIONS
// ============================================================================

/**
 * Get user's preferred feedback style
 */
export async function getFeedbackPreferences(userId) {
  const memory = await getUserMemory(userId);
  return memory?.engagement?.feedbackPreferences || {
    preferredTone: 'balanced',
    preferredLength: 'detailed',
    likesQuestions: true,
    likesChallenges: true,
    preferredInsightTypes: []
  };
}

/**
 * Get successful advice for a specific situation
 */
export async function getSuccessfulAdviceForSituation(userId, situation) {
  const memory = await getUserMemory(userId);
  if (!memory?.engagement?.successfulAdvice) return [];

  return memory.engagement.successfulAdvice
    .filter(advice => advice.situation === situation || advice.situation === 'general')
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 3);
}

/**
 * Get engagement statistics
 */
export async function getEngagementStats(userId) {
  const memory = await getUserMemory(userId);
  if (!memory) return null;

  const totalEngaged = memory.engagement?.engagedInsights?.length || 0;
  const totalDismissed = memory.engagement?.dismissedInsights?.length || 0;
  const engagementRate = totalEngaged + totalDismissed > 0
    ? (totalEngaged / (totalEngaged + totalDismissed))
    : 0;

  return {
    totalEngaged,
    totalDismissed,
    engagementRate: Number(engagementRate.toFixed(2)),
    preferredInsightTypes: memory.engagement?.feedbackPreferences?.preferredInsightTypes || [],
    successfulAdviceCount: memory.engagement?.successfulAdvice?.length || 0
  };
}

/**
 * Get user patterns (for pattern detection service)
 */
export async function getUserPatterns(userId) {
  const memory = await getUserMemory(userId);
  return memory?.patterns || null;
}

/**
 * Get user statistics
 */
export async function getStatistics(userId) {
  const memory = await getUserMemory(userId);
  return memory?.statistics || null;
}

/**
 * Check if patterns need refresh (older than 7 days)
 */
export async function arePatternsStale(userId) {
  if (!userId) return true;

  try {
    const memory = await getUserMemory(userId);
    if (!memory?.patterns?.lastAnalyzed) return true;

    const lastAnalyzed = memory.patterns.lastAnalyzed.toMillis ?
      memory.patterns.lastAnalyzed.toMillis() :
      new Date(memory.patterns.lastAnalyzed).getTime();

    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return lastAnalyzed < sevenDaysAgo;

  } catch (error) {
    logger.error('[AIMemory] Failed to check pattern staleness:', error);
    return true;
  }
}

/**
 * Get top recurring themes
 */
export function getTopThemes(patterns, limit = 5) {
  if (!patterns?.recurringThemes) return [];
  return patterns.recurringThemes
    .sort((a, b) => b.mentionCount - a.mentionCount)
    .slice(0, limit);
}

/**
 * Get top triggers
 */
export function getTopTriggers(patterns, limit = 5) {
  if (!patterns?.triggers) return [];
  return patterns.triggers
    .sort((a, b) => b.correlation - a.correlation)
    .slice(0, limit);
}

/**
 * Get top coping mechanisms
 */
export function getTopCopingMechanisms(patterns, limit = 5) {
  if (!patterns?.copingMechanisms) return [];
  return patterns.copingMechanisms
    .sort((a, b) => b.effectiveness - a.effectiveness)
    .slice(0, limit);
}

// ============================================================================
// GDPR COMPLIANCE
// ============================================================================

/**
 * Export all AI memory data
 */
export async function exportAIMemoryData(userId) {
  if (!userId) throw new Error('User ID is required');

  const memory = await getUserMemory(userId);
  return {
    exportedAt: new Date().toISOString(),
    userId,
    aiMemory: memory || null
  };
}

/**
 * Delete all AI memory data
 */
export async function deleteAIMemoryData(userId) {
  if (!userId) throw new Error('User ID is required');

  try {
    const { deleteDoc } = await import('firebase/firestore');

    // Delete from consolidated userAIMemory collection
    await deleteDoc(doc(db, COLLECTION_NAME, userId));

    logger.info('[AIMemory] Deleted all AI memory data for user:', userId);

  } catch (error) {
    logger.error('[AIMemory] Failed to delete AI memory data:', error);
    throw error;
  }
}

export default {
  getUserMemory,
  trackInsightEngagement,
  trackInsightDismissal,
  trackSuccessfulAdvice,
  trackUserIntention,
  updatePatterns,
  updateStatistics,
  getFeedbackPreferences,
  getSuccessfulAdviceForSituation,
  getEngagementStats,
  getUserPatterns,
  getStatistics,
  arePatternsStale,
  getTopThemes,
  getTopTriggers,
  getTopCopingMechanisms,
  exportAIMemoryData,
  deleteAIMemoryData
};
